// ./src/components/features/Events/controls/GuestModeField.tsx
//
// "How guests join" chooser — a full-width segmented control followed by a
// prominent, mode-specific explanation (GuestModeCallout) that spells out what
// the selected mode actually does (and can embed a walkthrough video). Shared
// (name, value) contract; reused by the Create Event wizard and details editor.

import React from 'react';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SegmentedField } from '@/components/generic';
import { FieldChangeHandler } from '@/components/generic';

import { GUEST_MODES, DEFAULT_GUEST_MODE } from './guestModeConfig';
import { GuestModeCallout } from './GuestModeCallout';

// Re-export the shared config so existing `@/components/features/Events` imports
// (GUEST_MODE, DEFAULT_GUEST_MODE, GUEST_MODES, …) keep working unchanged.
export * from './guestModeConfig';

export interface GuestModeFieldProps {
  name?: string;
  value?: string | null;
  onChange?: FieldChangeHandler<string>;
  /** Show the "How guests join" heading above the control (default true). */
  showLabel?: boolean;
  /** Show the per-mode explanation callout below the control (default true). */
  showHelp?: boolean;
  /** Invite-list only: jump to the event's guest list (passed to the callout). */
  onManageGuests?: () => void;
}

export const GuestModeField: React.FC<GuestModeFieldProps> = ({
  name,
  value,
  onChange,
  showLabel = true,
  showHelp = true,
  onManageGuests,
}) => {
  const { t } = useTranslation(['events']);
  const mode = value ?? DEFAULT_GUEST_MODE;

  return (
    <Stack gap="sm">
      {showLabel && (
        <Text fw={600} size="md" c="gray.8">
          {t('events:guestMode.label', { defaultValue: 'How guests join' })}
        </Text>
      )}
      <SegmentedField
        name={name}
        value={mode}
        onChange={onChange}
        fullWidth
        data={GUEST_MODES.map((m) => ({
          value: m.value,
          label: t(`events:guestMode.${m.value}`, { defaultValue: m.label }),
        }))}
      />
      {showHelp && <GuestModeCallout mode={mode} onManageGuests={onManageGuests} />}
    </Stack>
  );
};
